import { ExchangeRate } from '../../../features/exchange-rate/exchange-rate.service';
import { Account } from '../../../domain/account.domain';
import { BaseHolding } from '../../../domain/holding.domain';
import { SecuritySymbol } from '../../../domain/symbol.domain';
import { Currency } from '../../../domain/market.domain';

export const CIK_ERROR_CODE_PREFIX  = 'CIK.';
export const NO_AVAILABLE_QUANTITY_ERROR_CODE = 'CIK.NO.AVAILABLE.QUANTITY';
export const ACCOUNT_HAS_NO_SECURITIES_ERROR_CODE = 'CIK.ACCOUNT.HAS.NO.SECURITIES';

// Keeping SYSTEM_ONLINE_CODE to preserve compatibility with old api/mobile
export enum BackendCode {
  SYSTEM_ONLINE_CODE = 'WMM_API_I2609',
  SYSTEM_ONLINE = 'JOURNAL.SYSTEM.ONLINE'
}

export enum SecuritiesActiveStep {
  SELECT_ACCOUNTS = 'selectAccounts',
  SELECT_SECURITIES = 'selectSecurities',
  REVIEW = 'review',
  CONFIRM = 'confirm'
}

export enum TransferType {
  JOURNAL = 'JR',
  CIK = 'CK'
}

export enum ContributionType {
  PERSONAL = 'PERSONAL',
  SPOUSAL = 'SPOUSAL'
}

export enum SecuritiesTransfersBackendErrorCode {
  JOURNAL_INELIGIBLE_ACCOUNT = 'JOURNAL.INELIGIBLE.ACCOUNT',
  JOURNAL_INELIGIBLE_ASSETS = 'JOURNAL.INELIGIBLE.ASSETS',
  JOURNAL_EXCEEDS_ASSETS_MAXLIMIT = 'JOURNAL.EXCEEDS.ASSETS.MAXLIMIT',
  JOURNAL_EXCEEDS_TRANSFER_MAXLIMIT = 'JOURNAL.EXCEEDS.TRANSFER.MAXLIMIT',
  JOURNAL_LOAN_VALUE_EXCEEDED = 'JOURNAL.LOAN.VALUE.EXCEEDED',
  JOURNAL_ACCOUNT_TAX_RECIPIENT = 'JOURNAL.ACCOUNT.TAX.RECIPIENT',
  JOURNAL_ACCOUNT_NO_SECURITIES = 'JOURNAL.ACCOUNT.NO.SECURITIES',
  JOURNAL_ACCOUNT_GUARANTEE = 'JOURNAL.ACCOUNT.GUARANTEE',
  JOURNAL_ACCOUNT_HALT_TRADE = 'JOURNAL.ACCOUNT.HALT.TRADE',
  JOURNAL_ACCOUNT_HOLD_MAIL = 'JOURNAL.ACCOUNT.HOLD.MAIL',
  JOURNAL_ACCOUNT_COLLATERAL = 'JOURNAL.ACCOUNT.COLLATERAL',
  JOURNAL_ACCOUNT_FROZEN = 'JOURNAL.ACCOUNT.FROZEN',
  JOURNAL_ACCOUNT_CREDIT_RESTRICTED = 'JOURNAL.ACCOUNT.CREDIT.RESTRICTED',
  JOURNAL_ACCOUNT_SIN_MISMATCH = 'JOURNAL.ACCOUNT.SIN.MISMATCH',
  JOURNAL_ACCOUNT_SECURITIES_BUYING_POWER = 'JOURNAL.ACCOUNT.SECURITIES.BUYING.POWER',
  JOURNAL_ACCOUNT_FROM_NEGATIVE_CASH = 'JOURNAL.ACCOUNT.FROM.NEGATIVE.CASH',
  JOURNAL_SESSION_EXPIRED = 'JOURNAL.SESSION.EXPIRED',
  JOURNAL_ACCOUNT_JOINT_SIN_VALIDATION = 'JOURNAL.ACCOUNT.JOINT.SIN.VALIDATION',
  JOURNAL_USER_NON_RESIDENT = 'JOURNAL.USER.NON.RESIDENT',
  JOURNAL_USER_NON_RESIDENT_SANCTIONED = 'JOURNAL.USER.NON.RESIDENT.SANCTIONED',
  JOURNAL_ACCOUNT_NEGATIVE_CASH = 'JOURNAL.ACCOUNT.NEGATIVE.CASH',
  JOURNAL_ACCOUNT_NEGATIVE_BUYING_POWER = 'JOURNAL.ACCOUNT.NEGATIVE.BUYING.POWER',
  JOURNAL_ACCOUNT_NEGATIVE_EQUITY = 'JOURNAL.ACCOUNT.NEGATIVE.EQUITY',
  JOURNAL_ACCOUNT_NEGATIVE_MULTIPLE = 'JOURNAL.ACCOUNT.NEGATIVE.MULTIPLE',
  JOURNAL_USER_MISSING_DATE = 'JOURNAL.USER.MISSING.DATE',
  JOURNAL_USER_MAX_AGE = 'JOURNAL.USER.MAX.AGE',
  JOURNAL_ACCOUNT_INSUFFICIENT_MARGIN = 'JOURNAL.ACCOUNT.INSUFFICIENT.MARGIN',
  JOURNAL_FRACTIONAL_TRADING_NOT_ALLOWED = 'JOURNAL.FRACTIONAL.TRADING.NOT.ALLOWED',
  JOURNAL_ACCOUNT_SHORT = 'JOURNAL.ACCOUNT.SHORT'
}

export enum SecuritiesTransfersBackendWarningCode {
  JOURNAL_ACCOUNT_REVIEW_COLLATERAL = 'JOURNAL.ACCOUNT.REVIEW.COLLATERAL',
  JOURNAL_ACCOUNT_REVIEW_FROZEN = 'JOURNAL.ACCOUNT.REVIEW.FROZEN',
  JOURNAL_REVIEW_CIK_FINAL = 'JOURNAL.REVIEW.CIK.FINAL',
  JOURNAL_REVIEW_RDSP_CONTRIBUTION = 'JOURNAL.REVIEW.RDSP.CONTRIBUTION',
  JOURNAL_ACCOUNT_REVIEW_PRICE_UNAVAILABLE = 'JOURNAL.ACCOUNT.REVIEW.PRICE.UNAVAILABLE',
  JOURNAL_ACCOUNT_REVIEW_TOS = 'JOURNAL.ACCOUNT.REVIEW.TOS',
  JOURNAL_REVIEW_WITHDRAWAL_LIMIT = 'JOURNAL.REVIEW.WITHDRAWAL.LIMIT',
  JOURNAL_ACCOUNT_SHORT_FROM = 'JOURNAL.ACCOUNT.SHORT.FROM',
  JOURNAL_ACCOUNT_TO_NEGATIVE_CASH = 'JOURNAL.ACCOUNT.TO.NEGATIVE.CASH',
  JOURNAL_GOVERNMENT_GRANT_MATCHING = 'JOURNAL.GOVERNMENT.GRANT.MATCHING',
  JOURNAL_GOVERNMENT_GRANT_FAMILY_INCOME = 'JOURNAL.GOVERNMENT.GRANT.FAMILY.INCOME'
}

export enum SecuritiesTransfersBackendInfoCode {
  JOURNAL_INFO_RRSP = 'JOURNAL.INFO.RRSP',
  JOURNAL_INFO_TFSA = 'JOURNAL.INFO.TFSA',
  JOURNAL_INFO_SPOUSAL_RRSP = 'JOURNAL.INFO.SPOUSAL.RRSP',
  JOURNAL_INFO_RESP = 'JOURNAL.INFO.RESP',
  JOURNAL_INFO_RDSP = 'JOURNAL.INFO.RDSP'
}

export enum CikTransferStatusCode {
  JOURNAL_MANUAL_RDSP = 'JOURNAL.MANUAL.RDSP',
  JOURNAL_MANUAL_CREDIT_RDSP = 'JOURNAL.MANUAL.CREDIT.RDSP',
  JOURNAL_CREDIT_NON_STP = 'JOURNAL.CREDIT.NON.STP',
  JOURNAL_CONFIRM_CIK_SUCCESS = 'JOURNAL.CONFIRM.CIK.SUCCESS',
  JOURNAL_CONFIRM_IBM_OFFLINE_REJECT = 'JOURNAL.CONFIRM.IBM.OFFLINE.REJECT',
  JOURNAL_CONFIRM_IBM_OFFLINE_PROCESSING = 'JOURNAL.CONFIRM.IBM.OFFLINE.PROCESSING',
  JOURNAL_CONFIRM_MANUAL = 'JOURNAL.CONFIRM.MANUAL'
}

export enum SecuritiesTransfersFieldErrorCode {
  NOT_ELIGIBLE = 'notEligible',
  NO_SECURITIES = 'noSecurities',
  NO_AVAILABLE_QUANTITY = 'noAvailableQuantity',
  COLLATERAL = 'collateral',
  MARGIN_NO_BUYING_POWER = 'marginNoBuyingPower',
  NEGATIVE_BALANCE = 'negativeBalance',
  NEGATIVE_CASH = 'negativeCash',
  NEGATIVE_BUYING_POWER = 'negativeBuyingPower',
  NEGATIVE_EQUITY = 'negativeEquity',
  NEGATIVE_MULTIPLE = 'negativeMultiple'
}

export enum SecuritiesTransfersFormErrorCode {
  GENERIC_ERROR_MESSAGE = 'GENERIC_ERROR_MESSAGE',
  JOURNALS_OTHER_ERROR = 'JOURNALS_OTHER_ERROR',
  NO_FROM_ACCOUNTS = 'NO_FROM_ACCOUNTS',
  NO_TO_ACCOUNTS = 'NO_TO_ACCOUNTS',
  SECURITIES_EXCEED_LIMIT = 'SECURITIES_EXCEED_LIMIT',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  TAX_RECIPIENT_INVALID = 'TAX_RECIPIENT_INVALID',
  GUARANTEE = 'GUARANTEE',
  HALT_TRADE = 'HALT_TRADE',
  HOLD_MAIL = 'HOLD_MAIL',
  CREDIT_RESTRICTED = 'CREDIT_RESTRICTED',
  SIN_MISMATCH = 'SIN_MISMATCH',
  SIN_VALIDATION = 'SIN_VALIDATION',
  NO_VALID_DOB = 'NO_VALID_DOB',
  USER_MAX_AGE = 'USER_MAX_AGE',
  SHORT = 'SHORT',
  FROZEN = 'FROZEN',
  NON_RESIDENT = 'NON_RESIDENT',
  SANCTIONED = 'SANCTIONED'
}

export enum CikInfoMessage {
  DEFAULT = 'CIK_TRANSFER_TYPE_MESSAGE',
  RRSP = 'CIK_TRANSFER_TYPE_MESSAGE_RRSP',
  SRSP = 'CIK_TRANSFER_TYPE_MESSAGE_SRSP',
  TFSA = 'CIK_TRANSFER_TYPE_MESSAGE_TFSA',
  RDSP = 'CIK_TRANSFER_TYPE_MESSAGE_RDSP',
  RESP = 'CIK_TRANSFER_TYPE_MESSAGE_RESP'
}

export interface IBMExchangeRates  {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  IbmCadTOUsd: number;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  IbmUsdTOCad: number;
}

export interface Confirmation {
  success: boolean;
  referenceId: string;
  systemOnline: boolean;
  transactionDate: Date;
}

export interface Review {
  warningCodes: Array<string>;
  infoCodes: Array<string>;
}

export interface ValidateAccountResponse {
  transferToken: string;
}

export interface RetrieveAccountsResponse {
  fromAccounts: Array<SecuritiesTransfersAccount>;
  transferToken?: string;
}

export interface SecuritiesTransfersAccounts {
  accountId: string;
  transferToken: string;
  accounts: Array<SecuritiesTransfersAccount>;
  fromAccounts: Array<SecuritiesTransfersAccount>;
}

export interface SecuritiesTransfersAccount extends Account {
  fromAccount?: boolean;
  isSpousal?: boolean;
  totalAccountValue?: number;
  toAccounts?: Array<string>;
}

export interface TransferTypeResponse {
  transferToken: string;
  sameCurrency: boolean;
  transferLimit: number;
  positionsLimitPerAccount: number;
  positionsLimitPerTransfer: number;
  transferType: TransferType;
  status?: ResponseStatus;
  contributionTypeCd?: Array<ContributionType>;
}

export interface AgreementPortfolioResponse {
  fxRate: Array<ExchangeRate>;
  transferToken?: string;
  cashBalance?: CashBalance;
}

export interface CashBalance {
  value: number;
  eligiblity: boolean;
}

export interface SubmitTransferResponse {
  referenceId: string;
  status?: ResponseStatus;
}

export interface TransferStatusResponse {
  transferStatusCd: string;
  status?: ResponseStatus;
}

export interface ResponseStatus {
  serverStatusCode: string;
  severity: string;
  additionalStatus: Array<ServerStatus>;
}

export interface ServerStatus {
  statusCode: number;
  serverStatusCode: string;
  severity: string;
  statusDesc: string;
}

export interface SecuritiesTransfersHolding extends BaseHolding {
  securityId: string;
  fxRate: number;
  key: string;
  inputQuantity: number;
  fractionalTransferInd?: boolean;
}

export interface JournalRequest {
  validateOnly: boolean;
  transferToken: string;
  fromAccountId: string;
  toAccountId: string;
  exchangeRate: number;
  totalMarketValueCad: number;
  totalMarketValueUsd: number;
  cashValue: number;
  assetCollection: Array<Asset>;
}

export interface Asset {
  securityId: string;
  selectedQuantity: number;
}

export interface CikTransferRequestItem {
  fromAccountId: string;
  toAccountId: string;
  contributionType: string;
  totalQuantity: number;
  security: SecuritySymbol;
  securityName: string;
  quantity: number;
  lastPrice: number;
  fxRate: number;
  currency: Currency;
  securityId: string;
  cusip: string;
  key: string;
}

// New request format for CIK which combines old and new
// When old api is no longer required to be supported due to a feature flag, contributionRequest can be removed
export interface CikRequest {
  validateOnly: boolean;
  transferToken: string;
  fromAccountId: string;
  toAccountId: string;
  exchangeRate: number;
  totalMarketValueCad: number;
  totalMarketValueUsd: number;
  cashValue: number;
  assetCollection: Array<Asset>;
  contributionType: string;
  contributionRequest: Array<CikTransferRequestItem>;
}

